<script setup lang="ts">
import type { ComponentSize } from '~/types/elementsUi'
import { useInjectedColor } from '~/composables/useInjectedColor'
import { twMerge } from '~/utils/tw-utils'
import { NuxtLink } from '#components'
import type { NuxtLinkProps } from '#app'
import type { FAIconName } from '#shared/types/generated-icons'
import VHtmlButton from '~/components/atoms/button/VHtmlButton.vue'
import type {
  BaseTailwindColor,
  ButtonColor,
  ExtendedTailwindColor
} from '#shared/types/elementsUi'

interface BaseVButtonProps {
  color?: ButtonColor
  darkModeColor?: BaseTailwindColor
  size?: ComponentSize
  label?: string
  notRounded?: boolean
  outlined?: boolean
  border?: boolean
  loading?: boolean
  disabled?: boolean
  overrideColor?: boolean
  iconColor?: ExtendedTailwindColor
  width?: number | `${number}` | 'full'
  iconLeft?: FAIconName
  iconRight?: FAIconName
  iconOnly?: FAIconName
  form?: string
  ariaLabel?: string
}

interface ButtonProps extends BaseVButtonProps {
  to?: never
  target?: never
  download?: never
  forceExternal?: never
}

interface LinkProps extends BaseVButtonProps {
  to: NuxtLinkProps['to']
  target?: NuxtLinkProps['target']
  download?: string | boolean
  forceExternal?: boolean
}

const props = defineProps<ButtonProps | LinkProps>()

const { t, te } = useI18n()

const $field = useInjectedColor()

const darkModeColorMap: Record<BaseTailwindColor, string> = {
  primary: tw`dark:bg-primary dark:text-primary-text hover:dark:bg-primary`,
  secondary: tw`dark:bg-secondary dark:text-secondary-text hover:dark:bg-secondary`,
  neutral: tw`dark:bg-neutral dark:text-neutral-text hover:dark:bg-neutral`,
  info: tw`dark:bg-info dark:text-info-text hover:dark:bg-info`,
  success: tw`dark:bg-success dark:text-success-text hover:dark:bg-success`,
  warning: tw`dark:bg-warning dark:text-warning-text hover:dark:bg-warning`,
  danger: tw`dark:bg-danger dark:text-danger-text hover:dark:bg-danger`,
  white: tw`dark:bg-white dark:text-white-text hover:dark:bg-white`,
  gray: tw`dark:bg-gray dark:text-gray-text hover:dark:bg-gray`,
  black: tw`dark:bg-black dark:text-black-text hover:dark:bg-black`,
  'pure-black': tw`dark:bg-pure-black dark:text-pure-black-text hover:dark:bg-pure-black`,
  'dark-green': tw`dark:bg-dark-green dark:text-dark-green-text hover:dark:bg-dark-green`,
  beige: tw`dark:bg-beige dark:text-beige-text hover:dark:bg-beige`,
  pink: tw`dark:bg-pink dark:text-pink-text hover:dark:bg-pink`
}

const outlineColorMap: Record<BaseTailwindColor, string> = {
  primary: tw`text-primary hover:bg-primary hover:text-primary-text border-primary`,
  secondary: tw`text-secondary hover:bg-secondary hover:text-secondary-text border-secondary`,
  neutral: tw`text-neutral hover:bg-neutral hover:text-neutral-text border-neutral`,
  info: tw`text-info hover:bg-info hover:text-info-text border-info`,
  success: tw`text-success hover:bg-success hover:text-success-text border-success`,
  warning: tw`text-warning hover:bg-warning hover:text-warning-text border-warning`,
  danger: tw`text-danger hover:bg-danger hover:text-danger-text border-danger`,
  white: tw`text-white hover:bg-white hover:text-white-text border-white`,
  gray: tw`text-gray hover:bg-gray hover:text-gray-text border-gray`,
  black: tw`text-black hover:bg-black hover:text-black-text border-black`,
  'pure-black': tw`text-pure-black hover:bg-pure-black hover:text-pure-black-text border-pure-black`,
  'dark-green': tw`text-dark-green hover:bg-dark-green hover:text-dark-green-text border-dark-green`,
  beige: tw`text-beige hover:bg-beige hover:text-beige-text border-beige`,
  pink: tw`text-pink hover:bg-pink hover:text-pink-text border-pink`
}

const computedColor = computed(() => {
  let color: BaseTailwindColor | 'default' | 'transparent' | 'text' = 'default'
  if ($field.color?.value && !props.overrideColor) {
    color = $field.color.value
  }

  if (props.color) {
    color = props.color
  }

  let styles = tw`bg-${color} text-${color}-text hover:brightness-85`
  if (color === 'default') {
    styles = twMerge(
      styles,
      tw`bg-black text-black-text dark:bg-success dark:text-success-text hover:bg-black-800 hover:brightness-100 dark:hover:bg-success dark:hover:hover:brightness-85 `
    )
  }

  // TODO: Should the `text` colour replace `transparent`? Transparent feels more like what text+outlined should do
  if (props.outlined) {
    const isTransparent = color === 'transparent' || color === 'text'

    // Cannot use isTransparent check due to type inference
    if (color === 'transparent' || color === 'text' || color === 'default') {
      color = 'primary'
    }

    styles = twMerge(
      styles,
      tw`bg-transparent border border-solid ${outlineColorMap[color]} shadow-none hover:bg-primary-200 hover:shadow-xl hover:text-black hover:font-semibold focus-visible:text-black focus-visible:shadow-xl focus-visible:outline-none focus-visible:border-black-800 focus-visible:border-solid focus-visible:border-2`
    )

    if (isTransparent) {
      styles = twMerge(
        styles,
        tw`dark:text-white dark:border-white dark:hover:border-white dark:hover:bg-white-200 dark:focus-visible:bg-white-200 dark:focus-visible:border-primary`
      )
    }
  } else if (color === 'transparent') {
    styles = twMerge(
      styles,
      tw`bg-transparent text-primary dark:text-success shadow-none hover:brightness-100 hover:border hover:border-solid hover:border-primary dark:hover:border-success focus-visible:outline-none focus-visible:border-black-800 focus-visible:border-solid focus-visible:border-2`
    )
  } else if (color === 'text') {
    styles = twMerge(
      styles,
      tw`bg-transparent shadow-none underline font-semibold hover:font-bold`
    )
  } else {
    const outlineColor =
      props.outlined || color === 'black' || color === 'default'
        ? tw`focus-visible:outline-black-800 dark:focus-visible:outline-dark-green-950`
        : tw`focus-visible-outline-white-800 dark:focus-visible:outline-white-200`

    styles = twMerge(
      styles,
      tw`${outlineColor} focus-visible:outline-solid focus-visible:outline-2`
    )
  }

  if (props.border) {
    styles = twMerge(
      styles,
      tw`border border-solid border-black dark:border-default text-black dark:text-white hover:border-primary hover:text-primary dark:hover:text-success`
    )
  }

  if (props.darkModeColor) {
    styles = twMerge(styles, darkModeColorMap[props.darkModeColor])

    if (props.outlined) {
      styles = twMerge(styles, tw`dark:${outlineColorMap[props.darkModeColor]}`)
    }
  }

  return styles
})

const computedStyle = computed(() => {
  if (!props.width || props.width === 'full') {
    return
  }
  return { width: `${props.width}%`, maxWidth: `${props.width}%` }
})

const isExternalLink = computed(
  () =>
    typeof props.to === 'string' && (props.forceExternal || props.to.startsWith('http'))
)

const componentIsType = computed(() => {
  if (!props.to) {
    return VHtmlButton
  } else if (isExternalLink.value) {
    return 'a'
  } else {
    return NuxtLink
  }
})

const toProp = computed(() => {
  if (!props.to) {
    return undefined
  } else if (isExternalLink.value) {
    return {
      href: props.to
    }
  } else {
    return { to: props.to }
  }
})

const disabledProperty = computed(
  () => props.disabled ?? (!!props.to ? undefined : false)
)

const downloadProperty = computed(() => {
  if (props.download === undefined || props.download === false) {
    return undefined
  } else if (props.download === true) {
    return ''
  } else {
    return props.download
  }
})
</script>

<template>
  <component
    :is="componentIsType"
    v-bind="toProp"
    :target="target"
    :download="downloadProperty"
    :rel="target === '_blank' ? 'noopener noreferrer' : undefined"
    class="button"
    :form="form"
    :aria-label="ariaLabel && te(ariaLabel) ? t(ariaLabel) : ariaLabel"
    :class="[
      computedColor,
      size ?? 'normal',
      !notRounded && 'rounded-full',
      loading && 'loading',
      outlined && 'outlined',
      width === 'full' && 'w-full!',
      !!iconOnly && 'has-only-icon',
      disabled && 'opacity-50',
      disabled && to && 'pointer-events-none',
      to && 'not-prose'
    ]"
    :style="computedStyle"
    :disabled="disabledProperty"
    :aria-disabled="disabledProperty"
  >
    <slot name="icon-left">
      <VIcon v-if="iconLeft" :color="iconColor" :icon="iconLeft" />
    </slot>
    <slot name="icon-only">
      <VIcon v-if="iconOnly" :color="iconColor" :icon="iconOnly" :size="size" />
    </slot>
    <span
      v-if="(!iconOnly && label) || $slots['default']"
      class="button-label"
      data-no-narration
    >
      <slot> {{ label && te(label) ? t(label) : label }} </slot>
    </span>
    <slot name="icon-right">
      <VIcon v-if="iconRight" :color="iconColor" :icon="iconRight" />
    </slot>
  </component>
</template>

<style scoped></style>
